import { useEffect, useState } from "react";

import { Button, Callout } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { Hit } from "instantsearch.js";

import { Box, Flex } from "@/components/layout/flexbox";
import CancelBulkInviteSchedule from "@/components/pages_logged_in/workspace-admin/CancelBulkInviteSchedule";
import { InviteForm } from "@/components/pages_logged_in/workspace-admin/InviteForm";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { SUBTEXT_COLOR } from "@/css/constants";
import {
  InviteClientsMutation,
  useWorkspaceInvitationBatchLazyQuery,
} from "@/graphql";
import {
  WorkspaceInvitationBatch,
  WorkspaceScheduleJob,
} from "@/types/workspaces";
import { ensureNonNullArray, formatErrors } from "@/utils/array";

const INVITATION_BATCH_ID = "invitation_batch_id";

interface AlertInformationEmailsProps {
  emails: string[];
  informText: string;
  type: string;
}

function AlertInformationEmails({
  emails = [],
  informText = "",
  type = "",
}: AlertInformationEmailsProps) {
  return (
    <>
      <Box css={{ fontWeight: "bold", padding: "10px 0px" }}>
        {emails.length} {informText}
      </Box>
      <Flex css={{ color: SUBTEXT_COLOR }}>
        <StyledEmailList>
          {emails.map((email) => {
            return <li key={`${type}-${email}`}>{email}</li>;
          })}
        </StyledEmailList>
      </Flex>
    </>
  );
}

type InviteClientsResult = NonNullable<InviteClientsMutation["inviteClients"]>;

function Results({
  result,
  onClose,
}: {
  result: InviteClientsResult;
  onClose: () => void;
}) {
  const { ok, errors, toBeInvitedEmails, invalidEmails, existedEmails } =
    result || {};

  const errorMessage = formatErrors(errors) || "Something went wrong";

  return (
    <Box px={3}>
      <Callout intent={ok ? "success" : "warning"} icon="tick">
        {ok
          ? `${toBeInvitedEmails?.length} new users will be invited`
          : errorMessage}
      </Callout>
      <Box my={12}>
        <AlertInformationEmails
          emails={ensureNonNullArray(existedEmails)}
          informText="users are already in your workspace:"
          type="existedEmail"
        />
        <AlertInformationEmails
          emails={ensureNonNullArray(invalidEmails)}
          informText="emails were invalid and couldn’t be sent:"
          type="invalidEmails"
        />
      </Box>
      <Box mt={4}>
        <Button large={true} onClick={() => onClose()} loading={false}>
          Close
        </Button>
      </Box>
    </Box>
  );
}

interface BulkInviteScheduleDetailDialogProps {
  inviteSchedule: WorkspaceScheduleJob;
  afterAddedMember: () => void;
  afterModifyInvite: () => void;
  workspaceCredit?: string | null;
  onClose: () => void;
  allowSchedule?: boolean;
  scheduleText?: string;
  allowSetCredits?: boolean;
  allowSetGroup?: boolean;
}

export function BulkInviteScheduleDetailDialog({
  inviteSchedule,
  afterAddedMember,
  afterModifyInvite,
  workspaceCredit,
  onClose,
  allowSchedule = true,
  scheduleText,
  allowSetCredits = true,
  allowSetGroup = true,
}: BulkInviteScheduleDetailDialogProps) {
  const [result, setResult] = useState<InviteClientsResult>();
  const [isCancel, setIsCancel] = useState(false);
  const [workspaceInvitationBatch, setWorkspaceInvitationBatch] =
    useState<WorkspaceInvitationBatch | null>();

  const [fetch] = useWorkspaceInvitationBatchLazyQuery({
    onCompleted: (data) => {
      const workspaceInvitationBatchData = data?.workspaceInvitationBatch;
      const workspaceInvitationBatch = {
        workspaceInvitations: ensureNonNullArray(
          workspaceInvitationBatchData?.workspaceInvitations,
        ).map((invitation) => ({
          email: invitation?.email,
        })),
        credit: workspaceInvitationBatchData?.credit || "0.00",
        group: workspaceInvitationBatchData?.group,
        coaches: ensureNonNullArray(workspaceInvitationBatchData?.coaches).map(
          (coach) => {
            const hit: Hit = {
              objectID: coach.id,
              id: coach.id,
              name: coach.name,
              __position: 0,
            };
            return hit;
          },
        ),
        schedulingDetail: {
          scheduledJobId: inviteSchedule?.id,
          isSchedule: inviteSchedule?.status == "Scheduled",
          scheduleAt: inviteSchedule?.scheduledAt,
        },
      };
      setWorkspaceInvitationBatch(workspaceInvitationBatch);
    },
  });

  useEffect(() => {
    if (inviteSchedule) {
      const inviteScheduleJobPayload = JSON.parse(
        inviteSchedule?.jobPayload || "{}",
      );
      const workspaceInvitationBatchId =
        inviteScheduleJobPayload[INVITATION_BATCH_ID];
      if (workspaceInvitationBatchId) {
        fetch({
          variables: {
            workspaceInvitationBatchId: workspaceInvitationBatchId,
          },
        });
      }
    }
  }, [inviteSchedule]);

  return (
    <StyledDialog
      isOpen={!!inviteSchedule}
      lazy={true}
      title="Edit Scheduled Invitation"
      width="480px"
      onClose={() => {
        onClose();
      }}
      onOpening={() => {
        setResult(undefined);
      }}
      style={{ backgroundColor: "#FFF" }}
    >
      <Box py={24} px={2}>
        {result ? (
          <Results result={result} onClose={onClose} />
        ) : (
          <InviteForm
            onResultChange={(newResult) => {
              setResult(newResult);
              if (newResult?.ok) {
                afterAddedMember();
              }
            }}
            inline={true}
            allowSetCredits={allowSetCredits}
            workspaceCredit={workspaceCredit}
            isEdit={true}
            workspaceInvitationBatch={workspaceInvitationBatch}
            onCancel={() => setIsCancel(true)}
            allowSchedule={allowSchedule}
            scheduleText={scheduleText}
            allowSetGroup={allowSetGroup}
          />
        )}
      </Box>
      <CancelBulkInviteSchedule
        scheduleJobId={inviteSchedule?.id}
        isOpen={isCancel}
        onClose={() => setIsCancel(false)}
        onFail={() => {
          setIsCancel(false);
          setResult({
            ok: false,
            errors: [
              {
                message: "Failed to cancel this scheduled invitation",
              },
            ],
          });
        }}
        onSuccess={() => {
          onClose && onClose();
          afterModifyInvite && afterModifyInvite();
        }}
      />
    </StyledDialog>
  );
}

const StyledEmailList = styled("ul")`
  line-height: 160%;
  padding-left: 36px;
`;
